import { useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAdminLookup, useCreateLookup, useUpdateLookup, useDeleteLookup } from '../api/useAdmin'

const TABS = [
  { key: 'genres',      label: 'Жанры',              hasCode: false },
  { key: 'types',       label: 'Типы',               hasCode: true  },
  { key: 'statuses',    label: 'Статусы',             hasCode: true  },
  { key: 'age-ratings', label: 'Возрастной рейтинг', hasCode: true  },
]

function LookupTab({ type, hasCode }) {
  const { data: items = [], isLoading } = useAdminLookup(type)
  const { mutate: create, isPending: creating } = useCreateLookup(type)
  const { mutate: update } = useUpdateLookup(type)
  const { mutate: remove } = useDeleteLookup(type)

  const [newName, setNewName] = useState('')
  const [newCode, setNewCode] = useState('')
  const [editingCode, setEditingCode] = useState(null)
  const [editName, setEditName] = useState('')

  const inputCls = 'px-3 py-2 rounded-lg text-sm bg-surface2 border border-line text-fg focus:border-accent outline-none transition-colors'

  const handleCreate = (e) => {
    e.preventDefault()
    if (!newName.trim()) return
    const payload = { name: newName.trim(), ...(hasCode ? { code: newCode.trim() } : {}) }
    create(payload, {
      onSuccess: () => {
        setNewName('')
        setNewCode('')
        toast.success('Добавлено')
      },
      onError: (err) => toast.error(err.response?.data?.detail ?? 'Ошибка'),
    })
  }

  const startEdit = (item) => {
    setEditingCode(item.code)
    setEditName(item.name)
  }

  const handleUpdate = (item) => {
    if (!editName.trim() || editName.trim() === item.name) {
      setEditingCode(null)
      return
    }
    update({ code: item.code, name: editName.trim() }, {
      onSuccess: () => { setEditingCode(null); toast.success('Сохранено') },
      onError: (err) => toast.error(err.response?.data?.detail ?? 'Ошибка'),
    })
  }

  const handleDelete = (item) => {
    if (item.usage_count > 0) return
    remove(item.code, {
      onSuccess: () => toast.success('Удалено'),
      onError: (err) => toast.error(err.response?.data?.detail ?? 'Ошибка'),
    })
  }

  return (
    <div>
      <form onSubmit={handleCreate} className="flex flex-wrap gap-2 mb-6">
        {hasCode && (
          <input
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            placeholder="code (латиница)"
            className={`${inputCls} w-36`}
            required
          />
        )}
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Название"
          className={`${inputCls} flex-1 min-w-40`}
          required
        />
        <button
          type="submit"
          disabled={creating}
          className="px-4 py-2 rounded-lg text-sm font-semibold bg-accent text-on-accent hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          + Добавить
        </button>
      </form>

      {isLoading ? (
        <div className="text-sm text-muted py-8 text-center">Загрузка…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted py-8 text-center">Пусто</div>
      ) : (
        <div className="flex flex-col gap-1">
          {items.map((item) => (
            <div key={item.id} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-surface border border-line">
              {editingCode === item.code ? (
                <input
                  autoFocus
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onBlur={() => handleUpdate(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleUpdate(item)
                    if (e.key === 'Escape') setEditingCode(null)
                  }}
                  className={`${inputCls} flex-1`}
                />
              ) : (
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-fg">{item.name}</span>
                  {item.code && (
                    <span className="ml-2 text-xs text-muted font-mono">{item.code}</span>
                  )}
                </div>
              )}

              <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full border ${
                item.usage_count > 0
                  ? 'border-accent/30 text-accent'
                  : 'border-line text-muted'
              }`}>
                {item.usage_count} тайтл{item.usage_count === 1 ? '' : item.usage_count < 5 ? 'а' : 'ов'}
              </span>

              {editingCode !== item.code && (
                <button
                  onClick={() => startEdit(item)}
                  className="shrink-0 text-xs text-muted hover:text-fg transition-colors px-2 py-1"
                >
                  Изм.
                </button>
              )}

              <button
                onClick={() => handleDelete(item)}
                disabled={item.usage_count > 0}
                title={item.usage_count > 0 ? `Используется в ${item.usage_count} тайтлах` : 'Удалить'}
                className="shrink-0 text-xs text-red-400 hover:text-red-300 disabled:opacity-25 disabled:cursor-not-allowed transition-colors px-2 py-1"
              >
                Удалить
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function AdminLookups() {
  const [tab, setTab] = useState('genres')
  const current = TABS.find((t) => t.key === tab)

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Справочники</h1>
        <Link to="/admin" className="text-sm text-muted hover:text-fg transition-colors">
          ← Панель
        </Link>
      </div>

      <div className="flex gap-1 mb-6 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? 'border-accent text-accent'
                : 'border-transparent text-muted hover:text-fg'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <LookupTab key={tab} type={tab} hasCode={current.hasCode} />
    </div>
  )
}
